/**
 * StaffDashboardPage — operator console landing page.
 *
 * Wired against the live aggregator endpoints:
 *   - Mail dashboard stats:   GET /v1/mail-items/stats/dashboard
 *   - Sorting board:          GET /v1/sorting/board
 *   - Delivery dashboard:     GET /v1/delivery/dashboard
 *   - Delivery performance:   GET /v1/delivery/performance
 *   - Franchise central map:  GET /v1/franchise/dashboard/central/map
 *   - Approvals (existing):   /v1/supervisor-approvals + /v1/kyc/tier-upgrade-requests/stats + /v1/mail-items/address-change-requests/pending
 *   - Total Revenue:          GET /v1/finance/settlements?status=PAID&from=&to=  (sum netAmountMinor)
 *   - Transactions (today):   GET /v1/financial-services/pos/daily-volumes (today bucket)
 *   - Flagged Transactions:   GET /v1/reporting-ml/fraud-alerts/summary (openCount)
 *
 * Each KPI tile fails-soft: if its endpoint 401/403/5xxs the tile renders an
 * em-dash with a graceful "permission required" / "service unreachable" hint.
 */
import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  Clock,
  DollarSign,
  type LucideIcon,
  RefreshCw,
  Shield,
  Truck,
  Zap,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getDeliveryDashboard, getDeliveryPerformance } from '../../lib/api/delivery';
import { listSettlements } from '../../lib/api/finance';
import { getPosDailyVolumes } from '../../lib/api/financial-services';
import { getCentralMap } from '../../lib/api/franchise';
import { getTierUpgradeStats } from '../../lib/api/kyc';
import {
  getMailDashboardStats,
  getSortingBoard,
  listMailItems,
  listPendingAddressChangeRequests,
} from '../../lib/api/mail-items';
import { getFraudAlertSummary } from '../../lib/api/reporting-ml';
import { listSupervisorApprovals } from '../../lib/api/supervisorApprovals';
import { useMailHealth } from '../health/api/use-mail-health';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

interface StatCardProps {
  readonly title: string;
  readonly value: string;
  readonly icon: LucideIcon;
  readonly accent: 'green' | 'blue' | 'purple' | 'teal' | 'red' | 'gold';
  readonly hint?: string;
  readonly deferred?: boolean;
}

const ACCENT: Readonly<Record<StatCardProps['accent'], { bg: string; fg: string }>> = {
  green:  { bg: 'bg-nipost-green/10', fg: 'text-nipost-green' },
  blue:   { bg: 'bg-blue-50',         fg: 'text-blue-600' },
  purple: { bg: 'bg-purple-50',       fg: 'text-purple-600' },
  teal:   { bg: 'bg-teal-50',         fg: 'text-teal-600' },
  red:    { bg: 'bg-red-50',          fg: 'text-red-600' },
  gold:   { bg: 'bg-amber-50',        fg: 'text-amber-600' },
};

function StatCard({ title, value, icon: Icon, accent, hint, deferred }: StatCardProps): JSX.Element {
  const c = ACCENT[accent];
  return (
    <div className="bg-white rounded-xl border border-border p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-muted uppercase tracking-widest">
          {title}
        </span>
        <div className={`w-9 h-9 ${c.bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${c.fg}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
      {hint ? (
        <p className={`text-[11px] ${deferred ? 'text-amber-700' : 'text-muted'}`}>{hint}</p>
      ) : null}
    </div>
  );
}

function isDenied(query: { isError: boolean; error: unknown }): boolean {
  return (
    query.isError &&
    query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403)
  );
}

function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
   
  return `${(rate * 100).toFixed(1)}%`;
}

export function StaffDashboardPage(): JSX.Element {
  const health = useMailHealth();

  const mailQuery = useQuery({
    queryKey: ['dashboard', 'mail-items'],
    queryFn: () => listMailItems({ pageSize: 5 }),
    retry: false,
    staleTime: 30_000,
  });

  const mailStats = useQuery({
    queryKey: ['dashboard', 'mail-stats'],
    queryFn: () => getMailDashboardStats(7),
    retry: false,
    staleTime: 30_000,
  });

  const sortBoard = useQuery({
    queryKey: ['dashboard', 'sort-board'],
    queryFn: () => getSortingBoard(),
    retry: false,
    staleTime: 30_000,
  });

  const deliveryDash = useQuery({
    queryKey: ['dashboard', 'delivery-dashboard'],
    queryFn: () => getDeliveryDashboard(),
    retry: false,
    staleTime: 30_000,
  });

  const deliveryPerf = useQuery({
    queryKey: ['dashboard', 'delivery-performance'],
    queryFn: () => getDeliveryPerformance(30),
    retry: false,
    staleTime: 30_000,
  });

  const franchiseMap = useQuery({
    queryKey: ['dashboard', 'franchise-central-map'],
    queryFn: () => getCentralMap(),
    retry: false,
    staleTime: 30_000,
  });

  // Aggregate pending approvals across the platform. Each query is independent
  // and tolerant of failure — if one source 401/403/404s we still surface the
  // partial total rather than showing nothing.
  const supervisorPending = useQuery({
    queryKey: ['dashboard', 'approvals', 'supervisor'],
    queryFn: () => listSupervisorApprovals({ status: 'PENDING', pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const kycPending = useQuery({
    queryKey: ['dashboard', 'approvals', 'kyc'],
    queryFn: () => getTierUpgradeStats(),
    retry: false,
    staleTime: 30_000,
  });
  const addressChangePending = useQuery({
    queryKey: ['dashboard', 'approvals', 'address-change'],
    queryFn: () => listPendingAddressChangeRequests(),
    retry: false,
    staleTime: 30_000,
  });

  // ── New: live finance + reporting-ml tiles ────────────────────────────────
  // Total Revenue (MTD): sum netAmountMinor across PAID settlements for the
  // current calendar month. Uses YYYY-MM-DD date strings the settlement
  // endpoint expects.
  const monthBoundary = ((): { from: string; to: string } => {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth();
    const start = new Date(Date.UTC(y, m, 1));
    const end = new Date(Date.UTC(y, m + 1, 0));
    return {
      from: start.toISOString().slice(0, 10),
      to: end.toISOString().slice(0, 10),
    };
  })();
  const revenueQuery = useQuery({
    queryKey: ['dashboard', 'revenue-mtd', monthBoundary.from, monthBoundary.to],
    queryFn: () =>
      listSettlements({
        status: 'PAID',
        from: monthBoundary.from,
        to: monthBoundary.to,
        pageSize: 100,
      }),
    retry: false,
    staleTime: 60_000,
  });

  // Transactions (today): sum txnCount for today's bucket from POS daily volumes.
  const todayBoundary = ((): { from: string; to: string } => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    return { from: start.toISOString(), to: end.toISOString() };
  })();
  const transactionsTodayQuery = useQuery({
    queryKey: ['dashboard', 'transactions-today', todayBoundary.from],
    queryFn: () =>
      getPosDailyVolumes({ from: todayBoundary.from, to: todayBoundary.to, pageSize: 1 }),
    retry: false,
    staleTime: 60_000,
  });

  // Flagged Transactions: openCount from the new reporting-ml summary.
  const fraudSummaryQuery = useQuery({
    queryKey: ['dashboard', 'fraud-alert-summary'],
    queryFn: () => getFraudAlertSummary({}),
    retry: false,
    staleTime: 60_000,
  });

  const approvalsLoading =
    supervisorPending.isPending || kycPending.isPending || addressChangePending.isPending;
  const approvalsErrored =
    supervisorPending.isError && kycPending.isError && addressChangePending.isError;
   
  const approvalsTotal: number =
    (supervisorPending.data?.total ?? 0) +
    (kycPending.data?.pendingReview ?? 0) +
    (addressChangePending.data?.length ?? 0);

  const isPermissionDenied = isDenied(mailQuery);

  // ── Derived live values ────────────────────────────────────────────────
  const activeOutletsValue = franchiseMap.isPending
    ? '—'
    : franchiseMap.isError
    ? '—'
    : String(franchiseMap.data?.totalLive ?? 0);
  const activeOutletsHint = franchiseMap.isPending
    ? 'Loading…'
    : isDenied(franchiseMap)
    ? 'Permission required'
    : franchiseMap.isError
    ? 'Service unreachable'
    : 'Active outlets across all states';

  // ── Derived live values for new tiles ──────────────────────────────────
  // Sum kobo across PAID settlements; converted to NGN for display only.
  const revenueMinorTotal: number =
    revenueQuery.data?.items?.reduce((sum, s) => sum + (s?.netAmountMinor ?? 0), 0) ?? 0;
  const revenueDisplay = revenueQuery.isPending
    ? '— —'
    : revenueQuery.isError
    ? '— —'
    : (() => {
        const naira = revenueMinorTotal / 100;
        return naira.toLocaleString('en-NG', {
          style: 'currency',
          currency: 'NGN',
          maximumFractionDigits: 0,
        });
      })();
  const revenueHint = revenueQuery.isPending
    ? 'Loading…'
    : isDenied(revenueQuery)
    ? 'Permission required'
    : revenueQuery.isError
    ? 'Service unreachable'
    : 'Month-to-date settled revenue';

  const transactionsTodayValue = transactionsTodayQuery.isPending
    ? '—'
    : transactionsTodayQuery.isError
    ? '—'
    : String(transactionsTodayQuery.data?.periodTxnCount ?? 0);
  const transactionsTodayHint = transactionsTodayQuery.isPending
    ? 'Loading…'
    : isDenied(transactionsTodayQuery)
    ? 'Permission required'
    : transactionsTodayQuery.isError
    ? 'Service unreachable'
    : 'Counter transactions today';

  const flaggedValue = fraudSummaryQuery.isPending
    ? '—'
    : fraudSummaryQuery.isError
    ? '—'
    : String(fraudSummaryQuery.data?.openCount ?? 0);

  const onTimeValue = deliveryPerf.isPending
    ? '—'
    : deliveryPerf.isError
    ? '—'
    : formatPercent(deliveryPerf.data?.onTimeRate ?? null);
  const onTimeHint = deliveryPerf.isPending
    ? 'Loading…'
    : isDenied(deliveryPerf)
    ? 'Permission required'
    : deliveryPerf.isError
    ? 'Service unreachable'
    : '30-day on-time delivery rate';

  const avgServiceValue = (() => {
    if (deliveryPerf.isPending) return '—';
    if (deliveryPerf.isError) return '—';
    const v = deliveryPerf.data?.avgMinutesPerStop;
    if (v === null || v === undefined) return '—';
     
    return `${v.toFixed(1)}m`;
  })();
  const avgServiceHint = deliveryPerf.isPending
    ? 'Loading…'
    : isDenied(deliveryPerf)
    ? 'Permission required'
    : deliveryPerf.isError
    ? 'Service unreachable'
    : 'Average minutes per delivery stop, 30d';

  // ── Live ops strip ─────────────────────────────────────────────────────
   
  const itemsInTransit: number =
    (mailStats.data?.today?.byStatus?.OUT_FOR_DELIVERY ?? 0) +
    (mailStats.data?.today?.byStatus?.DISPATCHED ?? 0);

  const liveOps: readonly {
    readonly label: string;
    readonly dot: string;
    readonly color: string;
    readonly value: string;
    readonly deferred?: boolean;
  }[] = [
    {
      label: 'On-Route Agents',
      dot: 'bg-nipost-green',
      color: 'text-nipost-green',
      value: deliveryDash.isPending
        ? '—'
        : deliveryDash.isError
        ? '—'
        : String(deliveryDash.data?.activeShifts ?? 0),
    },
    {
      label: 'Items In Transit',
      dot: 'bg-blue-500',
      color: 'text-blue-600',
      value: mailStats.isPending ? '—' : mailStats.isError ? '—' : String(itemsInTransit),
    },
    {
      label: 'Sorting Queue',
      dot: 'bg-amber-400',
      color: 'text-amber-600',
      value: sortBoard.isPending
        ? '—'
        : sortBoard.isError
        ? '—'
        : String(sortBoard.data?.totalUnsorted ?? 0),
    },
    {
      label: 'Pending Approvals',
      dot: 'bg-purple-500',
      color: 'text-purple-600',
      value: approvalsLoading
        ? '—'
        : approvalsErrored
        ? '—'
        : approvalsTotal.toLocaleString('en-NG'),
    },
    {
      label: 'SLA Warnings',
      dot: 'bg-red-500',
      color: 'text-red-600',
      value: deliveryDash.isPending
        ? '—'
        : deliveryDash.isError
        ? '—'
        : String(deliveryDash.data?.failedStopsToday ?? 0),
    },
    {
      label: 'Flagged Transactions',
      dot: 'bg-rose-500',
      color: 'text-rose-600',
      value: flaggedValue,
      deferred: fraudSummaryQuery.isError,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operations Dashboard"
        description="Live operator console — KPIs aggregate from mail, delivery, franchise, and approvals services."
        actions={
          <button
            type="button"
            onClick={() => mailQuery.refetch()}
            disabled={mailQuery.isFetching}
            className="flex items-center gap-2 text-sm text-muted hover:text-gray-900 px-3 py-2 rounded-lg border border-border hover:border-nipost-green transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${mailQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* ── Hero Revenue + KPI Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Link
          to="/financial-services/transfers-ledger"
          data-testid="revenue-tile"
          className="bg-gradient-to-br from-nipost-green to-nipost-green-dark rounded-xl p-6 text-white flex flex-col justify-between min-h-[168px] hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-green-200 uppercase tracking-widest">
              Total Revenue
            </span>
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold leading-tight mt-4">{revenueDisplay}</p>
            <p className="text-sm text-green-200 mt-1">{revenueHint}</p>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/20">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-white/20 px-2 py-0.5 rounded-full">
              MTD · PAID settlements
            </span>
            <ArrowRight className="w-3 h-3 text-white/80" />
          </div>
        </Link>

        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/financial-services/pos"
            data-testid="transactions-tile"
            className="block"
          >
            <StatCard
              title="Transactions"
              value={transactionsTodayValue}
              icon={Activity}
              accent="blue"
              hint={transactionsTodayHint}
              deferred={transactionsTodayQuery.isError}
            />
          </Link>
          <StatCard
            title="Active Outlets"
            value={activeOutletsValue}
            icon={Building2}
            accent="purple"
            hint={activeOutletsHint}
            deferred={franchiseMap.isError}
          />
          <StatCard
            title="On-Time Delivery"
            value={onTimeValue}
            icon={Truck}
            accent="teal"
            hint={onTimeHint}
            deferred={deliveryPerf.isError}
          />
          <StatCard
            title="Open Approvals"
            value={
              approvalsLoading
                ? '—'
                : approvalsErrored
                ? '—'
                : approvalsTotal.toLocaleString('en-NG')
            }
            icon={AlertTriangle}
            accent="red"
            hint={
              approvalsLoading
                ? 'Loading…'
                : approvalsErrored
                ? 'Approval sources unreachable'
                : 'Pending supervisor, KYC and address-change requests'
            }
            deferred={approvalsErrored}
          />
          <StatCard
            title="Avg Service Time"
            value={avgServiceValue}
            icon={Clock}
            accent="gold"
            hint={avgServiceHint}
            deferred={deliveryPerf.isError}
          />
          <StatCard
            title="Mail Items (recent)"
            value={mailQuery.isPending ? '—' : isPermissionDenied ? '—' : String(mailQuery.data?.total ?? 0)}
            icon={Shield}
            accent="green"
            hint={
              mailQuery.isPending
                ? 'Loading…'
                : isPermissionDenied
                ? 'Permission required'
                : mailQuery.isError
                ? 'Service unreachable'
                : 'Mail items in current view'
            }
            deferred={isPermissionDenied || mailQuery.isError}
          />
        </div>
      </div>

      {/* ── Live Operations Pulse ── */}
      <div className="bg-white border border-border rounded-xl px-6 py-4">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-nipost-green" />
          <span className="text-xs font-bold text-gray-900 uppercase tracking-widest">
            Live Operations
          </span>
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="ml-auto text-[10px] text-muted">
            Mail, delivery and sorting · refreshed on focus
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
          {liveOps.map(({ label, dot, color, value }) => {
            const isFlagged = label === 'Flagged Transactions';
            const inner = (
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot}`} />
                <div>
                  <p className={`text-2xl font-bold leading-none ${color}`}>{value}</p>
                  <p className="text-[10px] text-muted mt-1 leading-tight">{label}</p>
                </div>
              </div>
            );
            if (isFlagged) {
              return (
                <Link
                  key={label}
                  to="/financial-services/fraud-alerts"
                  data-testid="flagged-tile"
                >
                  {inner}
                </Link>
              );
            }
            return <div key={label}>{inner}</div>;
          })}
        </div>
      </div>

      {/* ── Bottom Row: Recent shipments (live) + Approvals (live) + Health (live) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Shipments — live via GET /v1/mail-items */}
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">Recent Mail Items</h3>
              <p className="text-xs text-muted mt-0.5">Latest registered mail items</p>
            </div>
            <Link
              to="/mail/drafts"
              className="text-xs text-nipost-green hover:underline inline-flex items-center gap-1"
            >
              Drafts <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {mailQuery.isPending ? (
            <p className="text-xs text-muted">Loading mail items…</p>
          ) : isPermissionDenied ? (
            <DeferredNotice title="Permission required">
              Your account does not have permission to view mail items. Please contact your administrator.
            </DeferredNotice>
          ) : mailQuery.isError ? (
            <DeferredNotice title="Service unreachable">
              {(mailQuery.error as Error).message}
            </DeferredNotice>
          ) : (mailQuery.data?.items.length ?? 0) === 0 ? (
            <p className="text-xs text-muted py-6 text-center">
              No mail items yet. Use <Link to="/mail/register" className="text-nipost-green underline">Register Mail</Link> to create one.
            </p>
          ) : (
            <ul className="divide-y divide-border -mx-2">
              {mailQuery.data?.items.slice(0, 5).map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between px-2 py-3"
                >
                  <div className="min-w-0 pr-2">
                    <p className="text-xs font-bold text-gray-900 font-mono">{m.trackingNumber}</p>
                    <p className="text-[10px] text-muted mt-0.5">
                      {m.serviceClass} · operator #{m.operatorId}
                    </p>
                  </div>
                  <StatusBadge status={m.status} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Open approvals — live. Aggregates supervisor + KYC tier-upgrade +
            mail address-change pending counts across services. */}
        <div className="bg-white rounded-xl border border-border p-5" data-testid="approvals-tile">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">Open Approvals</h3>
              <p className="text-xs text-muted mt-0.5">
                Supervisor, KYC tier-upgrade and address-change queues
              </p>
            </div>
            <Link
              to="/approvals"
              className="text-xs text-nipost-green hover:underline inline-flex items-center gap-1"
            >
              Open queue <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {approvalsLoading ? (
            <p className="text-xs text-muted">Loading approval counts…</p>
          ) : approvalsErrored ? (
            <DeferredNotice title="Approval sources temporarily unavailable">
              The approval queues could not be reached. Please retry in a moment.
            </DeferredNotice>
          ) : (
            <div className="space-y-3">
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {approvalsTotal.toLocaleString('en-NG')}
                </p>
                <p className="text-xs text-muted">Pending across the platform</p>
              </div>
              <ul className="space-y-2 text-xs">
                <li className="flex items-center justify-between border-t border-border pt-2">
                  <span className="text-gray-700">Supervisor queue</span>
                  <span className="font-mono font-semibold text-gray-900">
                    {supervisorPending.isError
                      ? '—'
                      : (supervisorPending.data?.total ?? 0).toLocaleString('en-NG')}
                  </span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-gray-700">KYC tier upgrades</span>
                  <span className="font-mono font-semibold text-gray-900">
                    {kycPending.isError
                      ? '—'
                      : (kycPending.data?.pendingReview ?? 0).toLocaleString('en-NG')}
                  </span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-gray-700">Address-change requests</span>
                  <span className="font-mono font-semibold text-gray-900">
                    {addressChangePending.isError
                      ? '—'
                      : (addressChangePending.data?.length ?? 0).toLocaleString('en-NG')}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Platform Health — live, reused from Phase 0. */}
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-gray-900">Platform Health</h3>
              <p className="text-xs text-muted mt-0.5">Service readiness checks</p>
            </div>
          </div>

          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-muted">Mail service</p>
                <p className="text-xs font-semibold text-gray-900">Readiness check</p>
              </div>
              {health.isPending ? (
                <span className="text-xs text-muted">Checking…</span>
              ) : health.isError ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  FAIL
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-nipost-green">
                  <span className="w-2 h-2 rounded-full bg-nipost-green" />
                  OK
                </span>
              )}
            </div>
            {health.isError ? (
              <p className="text-[11px] text-red-600 mt-2">{health.error.message}</p>
            ) : health.data ? (
              <p className="text-[11px] text-muted mt-2">
                {health.data.service} {health.data.version}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
